/* AccountChip — header chip showing avatar / sign-in CTA + Pro badge.
 *
 * Tapped → opens AccountSheet (manage plan, sign out).
 */
import { useEffect, useState } from "react";
import { User, Sparkle, SignOut, Crown, X, ArrowSquareOut, Bell, BellSlash } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  isSubscribed as isPushSubscribed,
  pushSupported,
  subscribePush,
  unsubscribePush,
} from "@/lib/push";
import SignInSheet from "@/components/v2/SignInSheet";
import PaywallSheet from "@/components/v2/PaywallSheet";
import AlertsManagerInline from "@/components/v2/AlertsManagerInline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return null;
  }
}

function daysLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / 86400000));
}

export default function AccountChip({ theme, location }) {
  const { user, isPro, loading, signOut, refresh } = useAuth();
  const [openMenu, setOpenMenu] = useState(false);
  const [openSignIn, setOpenSignIn] = useState(false);
  const [openPaywall, setOpenPaywall] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!openMenu) return;
    if (!isPro) return;
    isPushSubscribed().then(setPushOn).catch(() => setPushOn(false));
  }, [openMenu, isPro]);

  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushOn) {
        await unsubscribePush();
        setPushOn(false);
        toast.success("Storm alerts off");
      } else {
        await subscribePush(location);
        setPushOn(true);
        toast.success("Storm alerts on — we'll ping you when severe weather loads");
      }
    } catch (e) {
      toast.error(e?.message || "Couldn't update push permission.");
    } finally {
      setPushBusy(false);
    }
  };

  const cancelAutoRenew = async () => {
    if (!window.confirm("Cancel auto-renew? Your remaining paid days are kept.")) return;
    try {
      await axios.post(`${API}/billing/cancel`, {}, { withCredentials: true });
      await refresh();
      toast.success("Auto-renew cancelled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel — please try again.");
    }
  };

  if (loading) return null;

  if (!user) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpenSignIn(true)}
          data-testid="account-chip-signin"
          aria-label="Sign in"
          className="flex items-center gap-1.5 px-2.5 h-[30px] rounded-full transition-all active:scale-95"
          style={{
            background: theme.accent,
            color: theme.id === "night" || theme.id === "storm" ? "#0f172a" : "#0f172a",
            border: `1px solid ${theme.accent}`,
            boxShadow: `0 4px 14px ${theme.accent}55`,
          }}
        >
          <Sparkle size={11} weight="fill" />
          <span className="text-[10px] font-bold uppercase tracking-[0.14em]">Try Pro</span>
        </button>
        <SignInSheet open={openSignIn} onClose={() => setOpenSignIn(false)} theme={theme} />
      </>
    );
  }

  // Signed-in chip — avatar circle + tiny Pro/trial dot
  const expIso = user.plan_expires_at;
  const dLeft = daysLeft(expIso);
  const trialActive = isPro && user.plan_source === "trial";
  const dotColor = isPro ? (trialActive ? "#10b981" : "#f59e0b") : "#94a3b8";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpenMenu(true)}
        data-testid="account-chip"
        aria-label="Account"
        className="relative flex items-center justify-center rounded-full transition-all active:scale-95"
        style={{
          width: 30,
          height: 30,
          background: theme.chip,
          border: `1px solid ${theme.surfaceBorder}`,
          color: theme.textSecondary,
          backdropFilter: "blur(10px)",
        }}
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.name || user.email}
            className="w-full h-full rounded-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <User size={14} weight="bold" />
        )}
        <span
          className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full"
          style={{ background: dotColor, border: `1.5px solid ${theme.surface}` }}
        />
      </button>

      <AnimatePresence>
        {openMenu && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-end justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            data-testid="account-sheet"
          >
            <motion.div
              className="absolute inset-0"
              style={{ background: "rgba(15,23,42,0.55)" }}
              onClick={() => setOpenMenu(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <motion.div
              className="relative w-full sm:max-w-[420px] rounded-t-[28px] px-6 pb-8 pt-5"
              style={{
                background:
                  theme.id === "night" || theme.id === "storm"
                    ? "#0f172a"
                    : "#fffbeb",
                border: `1px solid ${theme.surfaceBorder}`,
                boxShadow: "0 -20px 60px rgba(15,23,42,0.35)",
                paddingBottom: "calc(env(safe-area-inset-bottom) + 28px)",
              }}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 24, stiffness: 240 }}
            >
              <div className="flex items-center justify-between mb-5 relative">
                <div
                  className="w-10 h-1 rounded-full mx-auto"
                  style={{
                    background:
                      theme.id === "night" || theme.id === "storm"
                        ? "rgba(255,255,255,0.18)"
                        : "rgba(15,23,42,0.18)",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setOpenMenu(false)}
                  aria-label="Close account"
                  data-testid="account-sheet-close"
                  className="absolute top-0 right-0 w-8 h-8 rounded-full flex items-center justify-center"
                  style={{
                    background:
                      theme.id === "night" || theme.id === "storm"
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(15,23,42,0.06)",
                    color: theme.textSecondary,
                  }}
                >
                  <X size={12} weight="bold" />
                </button>
              </div>

              <div className="flex items-center gap-3 mb-5">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt=""
                    className="w-12 h-12 rounded-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: theme.chip, color: theme.textSecondary }}
                  >
                    <User size={18} weight="bold" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div
                    className="font-semibold text-[15px] truncate"
                    style={{ color: theme.textPrimary }}
                    data-testid="account-name"
                  >
                    {user.name || user.email.split("@")[0]}
                  </div>
                  <div
                    className="text-[11px] truncate"
                    style={{ color: theme.textMuted }}
                    data-testid="account-email"
                  >
                    {user.email}
                  </div>
                </div>
              </div>

              {/* Plan card */}
              <div
                className="rounded-2xl p-4"
                style={{
                  background: isPro
                    ? "linear-gradient(135deg, rgba(245,158,11,0.16) 0%, rgba(234,88,12,0.12) 100%)"
                    : "rgba(148,163,184,0.12)",
                  border: `1px solid ${isPro ? "rgba(245,158,11,0.3)" : "rgba(148,163,184,0.25)"}`,
                }}
                data-testid="account-plan-card"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Crown size={14} weight={isPro ? "fill" : "regular"}
                    style={{ color: isPro ? "#b45309" : theme.textMuted }} />
                  <span
                    className="text-[10px] font-bold uppercase tracking-[0.18em]"
                    style={{ color: isPro ? "#b45309" : theme.textMuted }}
                  >
                    {isPro ? (trialActive ? "Pro · trial" : "Pro · active") : "Free plan"}
                  </span>
                </div>
                {isPro ? (
                  <div className="text-[12px]" style={{ color: theme.textSecondary }}>
                    {trialActive ? "Trial ends" : "Renews"} <strong>{fmtDate(expIso)}</strong>
                    {dLeft != null && ` · ${dLeft} day${dLeft === 1 ? "" : "s"} left`}
                  </div>
                ) : (
                  <div className="text-[12px]" style={{ color: theme.textSecondary }}>
                    Hit-Rate, full Pulse, 7-day forecast and storm alerts are locked. Upgrade to unlock.
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  setOpenMenu(false);
                  setTimeout(() => setOpenPaywall(true), 220);
                }}
                data-testid="account-upgrade-btn"
                className="mt-4 w-full py-3 rounded-2xl font-semibold transition-transform active:scale-[0.985]"
                style={{
                  background: "linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)",
                  color: "#0f172a",
                  boxShadow: "0 8px 22px rgba(245,158,11,0.32)",
                }}
              >
                {isPro ? "Add 30 more days · ₹49" : "Upgrade · ₹49 / month"}
              </button>

              {/* Storm alerts toggle (Pro only). Browsers without PushManager
                  silently hide this row. */}
              {isPro && pushSupported() && (
                <button
                  type="button"
                  onClick={togglePush}
                  disabled={pushBusy}
                  data-testid="account-push-toggle"
                  className="mt-3 w-full px-4 py-3 rounded-2xl flex items-center gap-3 transition-transform active:scale-[0.985] disabled:opacity-70"
                  style={{
                    background: pushOn
                      ? "rgba(16,185,129,0.12)"
                      : theme.id === "night" || theme.id === "storm"
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(15,23,42,0.06)",
                    border: `1px solid ${pushOn ? "rgba(16,185,129,0.35)" : theme.surfaceBorder}`,
                  }}
                >
                  <span
                    className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{
                      background: pushOn ? "rgba(16,185,129,0.22)" : "rgba(148,163,184,0.18)",
                      color: pushOn ? "#047857" : theme.textMuted,
                    }}
                  >
                    {pushOn ? <Bell size={15} weight="fill" /> : <BellSlash size={15} weight="bold" />}
                  </span>
                  <div className="flex-1 min-w-0 text-left">
                    <div
                      className="text-[13px] font-semibold leading-tight"
                      style={{ color: theme.textPrimary }}
                    >
                      Storm-watch alerts
                    </div>
                    <div
                      className="text-[10.5px] leading-snug mt-0.5"
                      style={{ color: theme.textMuted }}
                    >
                      {pushOn
                        ? "Active · we'll push when severe weather loads"
                        : "Get a notification when storms build for your city"}
                    </div>
                  </div>
                  <span
                    className="shrink-0 text-[10px] font-bold uppercase tracking-[0.14em]"
                    style={{ color: pushOn ? "#047857" : theme.textMuted }}
                  >
                    {pushBusy ? "…" : pushOn ? "On" : "Off"}
                  </span>
                </button>
              )}

              {/* Per-location alert manager — only renders when push is on. */}
              {isPro && pushSupported() && pushOn && (
                <AlertsManagerInline theme={theme} currentLocation={location} />
              )}

              {user.razorpay_subscription_id && (
                <button
                  type="button"
                  onClick={cancelAutoRenew}
                  data-testid="account-cancel-renew"
                  className="mt-2 w-full py-2 text-[11px] font-medium underline-offset-2 hover:underline"
                  style={{ color: theme.textMuted }}
                >
                  Cancel auto-renew
                </button>
              )}

              <button
                type="button"
                onClick={async () => {
                  setOpenMenu(false);
                  await signOut();
                  toast.success("Signed out");
                }}
                data-testid="account-signout-btn"
                className="mt-3 w-full py-2.5 rounded-2xl text-[13px] font-medium flex items-center justify-center gap-2"
                style={{
                  background:
                    theme.id === "night" || theme.id === "storm"
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(15,23,42,0.06)",
                  color: theme.textSecondary,
                }}
              >
                <SignOut size={13} weight="bold" />
                Sign out
              </button>
              <a
                href="https://nowcast.krishnacosmic.com"
                target="_blank"
                rel="noreferrer"
                className="mt-3 flex items-center justify-center gap-1.5 text-[10px]"
                style={{ color: theme.textMuted }}
              >
                Nowcast by Cosmicflow <ArrowSquareOut size={10} weight="bold" />
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <PaywallSheet open={openPaywall} onClose={() => setOpenPaywall(false)} theme={theme} />
    </>
  );
}
